import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useConfirm } from '../ui/ConfirmDialog';

const BLANK_LEAVE_TYPE = {
  name: '',
  defaultDays: 20,
  color: '#4f46e5',
  paid: true
};

const BLANK_POLICY_DRAFT = {
  name: '',
  description: '',
  leaveTypes: [],
  copyFromTeamDefaults: false
};

const LeavePoliciesSettings = () => {
  const confirm = useConfirm();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [draft, setDraft] = useState(BLANK_POLICY_DRAFT);
  const [newLeaveType, setNewLeaveType] = useState(BLANK_LEAVE_TYPE);
  const [saving, setSaving] = useState(false);

  const loadPolicies = async () => {
    setLoading(true);
    try {
      const resp = await axios.get('/api/leave-policies');
      setPolicies(resp.data.policies || []);
    } catch (e) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPolicies(); }, []);

  const openCreate = () => {
    setEditingPolicy(null);
    setDraft(BLANK_POLICY_DRAFT);
    setNewLeaveType(BLANK_LEAVE_TYPE);
    setEditorOpen(true);
  };

  const openEdit = (policy) => {
    setEditingPolicy(policy);
    setDraft({
      name: policy.name || '',
      description: policy.description || '',
      leaveTypes: policy.leave_types || policy.leaveTypes || [],
      copyFromTeamDefaults: false
    });
    setNewLeaveType(BLANK_LEAVE_TYPE);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingPolicy(null);
    setDraft(BLANK_POLICY_DRAFT);
  };

  const addLeaveTypeToDraft = () => {
    if (!newLeaveType.name.trim()) {
      toast.error('Leave type name is required');
      return;
    }
    if (draft.leaveTypes.some(lt => lt.name.toLowerCase() === newLeaveType.name.toLowerCase())) {
      toast.error('A leave type with that name already exists in this policy');
      return;
    }
    setDraft(prev => ({
      ...prev,
      leaveTypes: [...prev.leaveTypes, { ...newLeaveType, id: Date.now().toString() }]
    }));
    setNewLeaveType(BLANK_LEAVE_TYPE);
  };

  const removeLeaveTypeFromDraft = (id) => {
    setDraft(prev => ({ ...prev, leaveTypes: prev.leaveTypes.filter(lt => lt.id !== id) }));
  };

  const savePolicy = async () => {
    if (!draft.name.trim()) {
      toast.error('Policy name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingPolicy) {
        await axios.put(`/api/leave-policies/${editingPolicy.id}`, {
          name: draft.name,
          description: draft.description,
          leaveTypes: draft.leaveTypes
        });
        toast.success('Policy updated');
      } else {
        await axios.post('/api/leave-policies', {
          name: draft.name,
          description: draft.description,
          leaveTypes: draft.leaveTypes,
          copyFromTeamDefaults: draft.copyFromTeamDefaults
        });
        toast.success('Policy created');
      }
      await loadPolicies();
      closeEditor();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const deletePolicy = async (policy) => {
    const ok = await confirm({
      title: `Delete policy "${policy.name}"?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/leave-policies/${policy.id}`);
      toast.success('Policy deleted');
      await loadPolicies();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete policy');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Leave Policies</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Leave policies let you give different groups of staff different leave allowances.
              Any team member without an assigned policy uses your team&apos;s default leave types.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Create Policy
          </button>
        </div>

        {loading ? (
          <p className="text-gray-500 dark:text-gray-400">Loading…</p>
        ) : policies.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-md text-center">
            <p className="text-gray-600 dark:text-gray-300">No leave policies yet.</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create your first policy to start assigning custom allowances to team members.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Leave Types</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Members</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {policies.map(p => (
                  <tr key={p.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {p.name}
                      {p.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-normal">{p.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {((p.leave_types || p.leaveTypes) || []).length} types
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                      {p.memberCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                      <button onClick={() => openEdit(p)} className="text-primary-600 hover:text-primary-800 dark:text-primary-400">Edit</button>
                      <button onClick={() => deletePolicy(p)} className="text-red-600 hover:text-red-900 dark:text-red-400">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editorOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {editingPolicy ? 'Edit Policy' : 'Create Policy'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                <input
                  type="text"
                  value={draft.name}
                  onChange={e => setDraft({ ...draft, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                  placeholder="e.g., Full-Time Enhanced"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description (optional)</label>
                <textarea
                  value={draft.description}
                  onChange={e => setDraft({ ...draft, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                />
              </div>

              {!editingPolicy && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={draft.copyFromTeamDefaults}
                    onChange={e => setDraft({ ...draft, copyFromTeamDefaults: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Start from team default leave types
                  </label>
                </div>
              )}

              {/* Leave types in this policy */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Leave Types in this Policy</h4>
                {draft.leaveTypes.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No leave types yet. Add one below.</p>
                ) : (
                  <ul className="space-y-2">
                    {draft.leaveTypes.map(lt => (
                      <li key={lt.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded px-3 py-2">
                        <span className="flex items-center text-sm">
                          <span className="h-3 w-3 rounded-full mr-2" style={{ backgroundColor: lt.color }} />
                          <span className="font-medium text-gray-900 dark:text-white">{lt.name}</span>
                          <span className="ml-2 text-gray-500 dark:text-gray-400">— {lt.defaultDays} days, {lt.paid ? 'paid' : 'unpaid'}</span>
                        </span>
                        <button onClick={() => removeLeaveTypeFromDraft(lt.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 text-sm">Remove</button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Add form */}
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Leave type name"
                      value={newLeaveType.name}
                      onChange={e => setNewLeaveType({ ...newLeaveType, name: e.target.value })}
                      className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    />
                    <input
                      type="number"
                      placeholder="Days"
                      min="0"
                      max="365"
                      value={newLeaveType.defaultDays}
                      onChange={e => setNewLeaveType({ ...newLeaveType, defaultDays: Number(e.target.value) })}
                      className="block w-full rounded-md border-gray-300 shadow-sm sm:text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <input
                      type="color"
                      value={newLeaveType.color}
                      onChange={e => setNewLeaveType({ ...newLeaveType, color: e.target.value })}
                      className="h-8 w-8 rounded"
                    />
                    <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={newLeaveType.paid}
                        onChange={e => setNewLeaveType({ ...newLeaveType, paid: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2">Paid</span>
                    </label>
                    <button
                      type="button"
                      onClick={addLeaveTypeToDraft}
                      className="ml-auto inline-flex py-1 px-3 text-sm rounded-md bg-primary-600 hover:bg-primary-700 text-white"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2">
              <button onClick={closeEditor} className="py-2 px-4 border border-gray-300 dark:border-gray-500 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button
                onClick={savePolicy}
                disabled={saving}
                className="py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : (editingPolicy ? 'Save Changes' : 'Create Policy')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeavePoliciesSettings;
